'use client'
import { Spinner } from '@/components/ui/spinner'
import { fetchPaymentPortal } from '@/lib/api'
import {
  useGetUserQuery,
  useGetUserQuotaQuery,
  useSignOutMutation,
} from '@/lib/features/user/userApi'
import { PAGE_PATH } from '@/lib/page-paths'
import {
  hasInfiniteAppCount,
  hasInfiniteEditCredits,
  hasInfiniteExecuteCredits,
} from '@/lib/subscription-utils'
import { AnimatePresence, motion } from 'framer-motion'
import { CreditCard, LogOut, User } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Button } from '../ui/button'

export function UserProfileDropdown() {
  const { data: userData, isLoading: userLoading, error: userError } = useGetUserQuery()
  const {
    data: quota,
    refetch: refetchQuota,
    isLoading: quotaLoading,
    error: quotaError,
  } = useGetUserQuotaQuery()
  const [signOutMutation] = useSignOutMutation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  const [isBillingLoading, setIsBillingLoading] = useState(false)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleProjectClick = () => {
    router.push(PAGE_PATH.DASHBOARD)
  }

  useEffect(() => {
    refetchQuota()
  }, [isOpen, refetchQuota])

  const handleSignOut = async () => {
    await signOutMutation()
    router.push(PAGE_PATH.HOME)
    setIsOpen(false)
  }

  const handleBillingClick = async () => {
    if (user.isAnonymous) {
      toast.error('You must sign in to manage your billing')
      return
    }

    if (quota?.subscription.name === 'Free') {
      router.push(PAGE_PATH.PLANS)
      return
    }

    setIsBillingLoading(true)
    try {
      const portalUrl = await fetchPaymentPortal('')
      if (portalUrl) {
        window.location.href = portalUrl
      }
    } catch (error) {
      console.error('Error fetching payment portal:', error)
      toast.error('Error fetching payment portal')
      setIsBillingLoading(false)
    }

    setTimeout(() => {
      setIsBillingLoading(false)
    }, 2000)
  }

  // Show loading state
  if (userLoading) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-full">
        <div className="h-8 w-8 animate-pulse rounded-full bg-gray-400"></div>
      </div>
    )
  }

  // Show error state
  if (userError) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-red-300 bg-red-100">
        <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
    )
  }

  if (!userData) return null

  const user = userData.user

  if (!user || user.isAnonymous) return null

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-white/30 transition-colors hover:border-white/60"
      >
        {user.image ? (
          <Image
            src={user.image}
            alt={
              user.name ? user.name.charAt(0).toUpperCase() : user.email!.charAt(0).toUpperCase()
            }
            width={40}
            height={40}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#48039E] to-[#7F039E] font-medium text-white">
            {user.name ? user.name.charAt(0).toUpperCase() : user.email!.charAt(0).toUpperCase()}
          </div>
        )}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{
              transformOrigin: 'top right',
              opacity: 0,
              scale: 0,
              rotate: -15,
            }}
            animate={{
              transformOrigin: 'top right',
              opacity: 1,
              scale: 1,
              rotate: 0,
            }}
            exit={{
              opacity: 0,
              scale: 0,
              rotate: -15,
            }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1],
              rotate: {
                duration: 0.2,
              },
            }}
            className="absolute right-0 top-12 z-50 flex w-[332px] flex-col items-start gap-2 rounded-lg bg-black/50 p-4 backdrop-blur-xl"
          >
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex w-full -translate-y-2.5 items-center rounded-lg p-1 px-2 opacity-0"
            >
              <div className="flex flex-col items-start justify-center">
                <span className="font-hanken-grotesk text-base font-medium text-white">
                  {user.name || user.email!.split('@')[0]}
                </span>
                <span className="font-inter text-sm font-medium text-white/60">{user.email}</span>
              </div>
            </motion.div>

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="relative flex w-full translate-y-2.5 flex-col gap-2 rounded-lg border border-white/30 p-4 opacity-0"
            >
              {quotaLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  <span className="ml-2 text-sm text-white/60">Loading usage...</span>
                </div>
              ) : quotaError ? (
                <div className="flex flex-col items-center justify-center py-4 text-red-400">
                  <svg
                    className="mb-1 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-center text-xs">Failed to load usage data</span>
                </div>
              ) : quota ? (
                <>
                  <div className="flex items-center justify-between">
                    <p className="font-inter text-sm font-medium leading-[140%] text-white">Apps</p>
                    <p className="font-inter text-sm font-medium leading-[140%] text-white">
                      {quota.appCount}
                      {!hasInfiniteAppCount(quota) && ` / ${quota.subscription.maxApps}`}
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="font-inter text-sm font-medium leading-[140%] text-white">
                      App Edits
                    </p>
                    <p className="font-inter text-sm font-medium leading-[140%] text-white">
                      {hasInfiniteEditCredits(quota) ? 'unlimited' : quota.editCredits}
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="font-inter text-sm font-medium leading-[140%] text-white">
                      Run Credits
                    </p>
                    <p className="font-inter text-sm font-medium leading-[140%] text-white">
                      {hasInfiniteExecuteCredits(quota) ? 'unlimited' : quota.executeCredits}
                    </p>
                  </div>

                  {quota.subscription.name !== 'Launch' && (
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        className="mt-2 w-full"
                        variant="secondary"
                        onClick={() => router.push(PAGE_PATH.PLANS)}
                      >
                        Upgrade
                      </Button>
                    </motion.div>
                  )}
                </>
              ) : null}
            </motion.div>

            <motion.div
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="my-2 h-px w-full border-t border-white/30 opacity-0"
            />

            <motion.button
              whileHover={{ x: 5 }}
              whileTap={{ scale: 0.98 }}
              className="flex w-full flex-row items-center gap-2 rounded-lg p-2 transition-colors hover:bg-white/10"
              onClick={handleProjectClick}
            >
              <User className="h-6 w-6 text-white" />
              <span className="font-inter text-sm font-semibold text-white">
                Profile & Projects
              </span>
            </motion.button>

            <motion.button
              whileHover={{ x: 5 }}
              whileTap={{ scale: 0.98 }}
              className="flex w-full flex-row items-center gap-2 rounded-lg p-2 transition-colors hover:bg-white/10"
              onClick={handleBillingClick}
            >
              {isBillingLoading && <Spinner variant="default" size={32} overlay={true} />}
              <CreditCard className="h-6 w-6 text-white" />
              <span className="font-inter text-sm font-semibold text-white">Billing</span>
            </motion.button>

            <motion.button
              whileHover={{ x: 5 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSignOut}
              className="flex w-full flex-row items-center gap-2 rounded-lg p-2 transition-colors hover:bg-white/10"
            >
              <LogOut className="h-6 w-6 text-white" />
              <span className="font-inter text-sm font-semibold text-white">Sign Out</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
